import { ReactNode, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AiQualificationCard, Tabs } from '@/components'
import {
  CreditOrder,
  Header,
  ProfileShowDocuments,
  ProfileShowGeneral,
  ProfileShowOrder,
  Quotation,
  QuotationRequest,
  ProfileShowBuyerUsers,
} from './components'
import { ProfileDrawerForm } from './components/general/components'

import { useParamsState } from '@/helpers'
import { ConversationsAcquisition } from '@/pages/ai-center/conversations/acquisition'

import { useProfileDetail, useUserList } from '@/services'

import { ModuleKey } from './show.type'
import styles from './show.module.css'

import { QuotationRequestAiQualificationDataProps } from '@/types'

const translations: Record<ModuleKey, string> = {
  all: 'tablesButtonAll',
  'quotation-request': 'tablesButtonRFQs',
  documents: 'tablesButtonDocuments',
  quotation: 'tablesButtonQuotations',
  order: 'tablesButtonOrders',
  users: 'tablesButtonUsers',
  ai: 'tablesButtonAi',
  credit: 'tablesButtonCredit',
}

export const ProfileShowV2 = () => {
  const { t } = useTranslation('screenProfileShow')
  const { uuid } = useParams()

  const [showModule, setShowModule] = useParamsState({
    tab: 'all',
  })
  const [isOpenGeneralInfosForm, setIsOpenGeneralInfosForm] = useState(false)

  const { data, isLoading, refetch } = useProfileDetail(uuid)

  const [usersSearchTerm, setUsersSearchTerm] = useState<string>('')

  const { data: dataUsers, isLoading: isLoadingUsersSearch } = useUserList({
    params: {
      limit: 50,
      page: 1,
      isStaff: 'true',
      search: usersSearchTerm,
    },
  })

  const getPhoneNumbers = () => {
    const users = data?.data?.users?.filter((user) => user.phone)

    if (!users?.length) return ['undefined']

    return users?.map((user) => user.phone)
  }

  const objectsRoute: Record<string, () => ReactNode> = {
    all: () => (
      <ProfileShowGeneral
        isLoading={isLoading}
        profile={data?.data}
        profileRefetch={refetch}
        setIsOpenGeneralInfosForm={setIsOpenGeneralInfosForm}
      />
    ),
    'quotation-request': () => (
      <QuotationRequest
        buyerProfileUuid={uuid || ''}
        kams={dataUsers?.data?.results}
        isLoadingKams={isLoadingUsersSearch}
        setSearchKams={setUsersSearchTerm}
      />
    ),
    quotation: () => (
      <Quotation
        buyerProfileUuid={uuid || ''}
        kams={dataUsers?.data?.results}
        isLoadingKams={isLoadingUsersSearch}
        setSearchKams={setUsersSearchTerm}
      />
    ),
    order: () => (
      <ProfileShowOrder
        buyerProfileUuid={uuid || ''}
        kams={dataUsers?.data?.results}
        isLoadingKams={isLoadingUsersSearch}
        setSearchKams={setUsersSearchTerm}
      />
    ),
    documents: () => <ProfileShowDocuments profile={data?.data} />,
    credit: () => <CreditOrder profile={data?.data} />,
    ai: () => (
      <ConversationsAcquisition
        initialFilters={{ buyerPhones: getPhoneNumbers() }}
      />
    ),
    users: () => <ProfileShowBuyerUsers profileUuid={uuid} />,
  }

  return (
    <>
      <div className={styles.core}>
        <Header
          profile={data?.data}
          setIsOpenGeneralInfosForm={setIsOpenGeneralInfosForm}
        />

        <AiQualificationCard
          aiQualificationData={
            data?.data?.leads?.[0]?.aiQualificationData as
              | QuotationRequestAiQualificationDataProps
              | undefined
          }
          isLoading={isLoading}
        />

        <div className={styles.tables}>
          <Tabs
            items={Object.keys(objectsRoute).map((key) => ({
              key,
              title: t(translations[key as ModuleKey]),
              isActive: (showModule.tab || 'all') === key,
              onSelect: () => setShowModule({ tab: key as ModuleKey }),
            }))}
            hasCount={false}
          />

          {objectsRoute?.[(showModule.tab as string) || 'all']()}
        </div>
      </div>

      <ProfileDrawerForm
        isOpen={isOpenGeneralInfosForm}
        onHandleOpen={setIsOpenGeneralInfosForm}
        profileUuid={uuid}
      />
    </>
  )
}
